import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  onAuthStateChanged,
  signOut,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth, isFirebaseConfigured } from '@/lib/firebase';
import { isPreviewMode, DEMO_ACCOUNT_ID } from '@/lib/previewMode';

interface AuthClaims {
  admin?: boolean;
  accountId?: string;
}

/**
 * Auth store. In preview mode (VITE_PREVIEW_MODE=1) returns a faked signed-in
 * user pointed at the demo account so /account/* is browseable without Firebase.
 * Without preview mode and without Firebase configured, stays signed-out and the
 * router redirects /account/* → /login.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const claims = ref<AuthClaims>({});
  const ready = ref(false);
  const error = ref<string | null>(null);

  const isSignedIn = computed(() => previewMode || !!user.value);
  const isAdmin = computed(() => !!claims.value.admin);
  const accountId = computed(() => claims.value.accountId ?? null);
  const userEmail = computed(() => (user.value?.email ?? (previewMode ? 'sarah@maplegrovedental.com' : null)));

  const previewMode = isPreviewMode();

  async function refreshClaims(u: User | null) {
    if (!u) { claims.value = {}; return; }
    const token = await u.getIdTokenResult(true);
    claims.value = {
      admin: token.claims.admin === true,
      accountId: typeof token.claims.accountId === 'string' ? token.claims.accountId : undefined,
    };
  }

  function init() {
    if (ready.value) return;
    if (previewMode) {
      claims.value = { accountId: DEMO_ACCOUNT_ID, admin: false };
      ready.value = true;
      return;
    }
    if (!isFirebaseConfigured()) { ready.value = true; return; }
    onAuthStateChanged(getFirebaseAuth(), async (u) => {
      user.value = u;
      await refreshClaims(u);
      ready.value = true;
    });
  }

  async function logout() {
    if (previewMode) return; // Preview "logout" is a no-op so the demo doesn't break.
    await signOut(getFirebaseAuth());
    user.value = null;
    claims.value = {};
  }

  return {
    user,
    claims,
    ready,
    error,
    isSignedIn,
    isAdmin,
    accountId,
    userEmail,
    previewMode,
    init,
    logout,
    refreshClaims,
  };
});
